import { type JSX, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import {
  MobileHeading,
  NavTabsContentContainer,
} from "features/layout/components";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile } from "common/types";

import { CAMPAIGNS_TAB_ITEMS } from "../../constants";
import useBlacklist from "../../datahooks/useBlacklist";
import AddToBlacklistButton from "./AddToBlacklistButton/AddToBlacklistButton";
import BlacklistAccountsTable from "./DesktopLayout/BlacklistAccountsTable";
import BlacklistCompaniesTable, {
  type BlacklistCompanyRow,
} from "./DesktopLayout/BlacklistCompaniesTable";
import BlacklistMobileList from "./MobileLayout/BlacklistMobileList";
import ManageBlacklist from "./MobileLayout/ManageBlacklist";

import type { Company } from "../../types";

import EmptyStateImage from "assets/images/empty-placeholders/blacklist.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

interface BlacklistProps {
  blacklistType: "accounts" | "companies";
}

export default function Blacklist({ blacklistType }: BlacklistProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState<LinkedInProfile[]>(
    [],
  );
  const [selectedCompanies, setSelectedCompanies] = useState<
    BlacklistCompanyRow[]
  >([]);
  const { pathname } = useLocation();
  const { blacklist, isLoadingBlacklist, isBlacklistError, refetchBlacklist } =
    useBlacklist<typeof blacklistType>(blacklistType);

  useEffect(() => {
    setSelectedAccounts([]);
    setSelectedCompanies([]);
  }, [blacklistType]);

  const isAccountsBlacklist = blacklistType === "accounts";
  const isCompaniesSelected = pathname.endsWith("companies");

  const filteredBlacklist = useMemo(() => {
    if (!blacklist?.length) {
      return [];
    }
    return blacklist.filter((blacklistItem) => {
      const string =
        "full_name" in blacklistItem
          ? blacklistItem.full_name
          : blacklistItem.name;
      return string.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [blacklist, searchTerm]);

  const companyRows: BlacklistCompanyRow[] = useMemo(() => {
    if (blacklistType !== "companies") {
      return [];
    }
    return (filteredBlacklist as Company[]).map((c) => ({
      ...c,
      id: c.urn,
    }));
  }, [blacklistType, filteredBlacklist]);

  const accountRows: LinkedInProfile[] = useMemo(() => {
    if (blacklistType !== "accounts") {
      return [];
    }
    return filteredBlacklist as LinkedInProfile[];
  }, [blacklistType, filteredBlacklist]);

  let mainComponent: JSX.Element;

  if (isBlacklistError) {
    mainComponent = (
      <ErrorPlaceholder
        onRetry={refetchBlacklist}
        errorMessage="Could not display blacklist"
      />
    );
  } else if (isLoadingBlacklist) {
    mainComponent = <Loader />;
  } else if (!blacklist || !blacklist.length) {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyStateImage}
        title={`No blacklisted ${blacklistType}`}
        subtitle={`Blacklist ${blacklistType} to exclude them from all current and future campaigns`}
        actionComponent={
          <AddToBlacklistButton
            type={blacklistType}
            size="md"
            isEmptyPlaceholderButton={false}
          />
        }
      />
    );
  } else if (filteredBlacklist.length === 0) {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={NoResultsImage}
        title={`No ${blacklistType} found`}
        subtitle="There were no results that match your criteria"
        actionComponent={
          <Button onClick={() => setSearchTerm("")}>Clear</Button>
        }
      />
    );
  } else if (isDesktop) {
    mainComponent =
      blacklistType === "accounts" ? (
        <BlacklistAccountsTable
          profiles={accountRows}
          selectedRows={selectedAccounts}
          setSelectedRows={setSelectedAccounts}
          isLoading={false}
        />
      ) : (
        <BlacklistCompaniesTable
          companies={companyRows}
          selectedRows={selectedCompanies}
          setSelectedRows={setSelectedCompanies}
          isLoading={false}
        />
      );
  } else {
    mainComponent =
      blacklistType === "accounts" ? (
        <BlacklistMobileList
          blacklistType="accounts"
          profiles={accountRows}
          selectedProfiles={selectedAccounts}
          setSelectedProfiles={setSelectedAccounts}
        />
      ) : (
        <BlacklistMobileList
          blacklistType="companies"
          companies={companyRows}
          selectedCompanies={selectedCompanies}
          setSelectedCompanies={setSelectedCompanies}
        />
      );
  }

  const selectedItemsCount =
    blacklistType === "accounts"
      ? selectedAccounts.length
      : selectedCompanies.length;

  const navigationComponent = (
    <div className="flex rounded-full p-1 bg-black-100 my-auto">
      <Button
        asChild
        size={isDesktop ? "xs" : "sm"}
        className="w-1/2 lg:w-auto"
        variant={!isCompaniesSelected ? "outline-black" : "ghost-black"}
      >
        <NavLink to="../blacklist">Accounts</NavLink>
      </Button>

      <Button
        asChild
        size={isDesktop ? "xs" : "sm"}
        className="w-1/2 lg:w-auto"
        variant={isCompaniesSelected ? "outline-black" : "ghost-black"}
      >
        <NavLink to="../blacklist/companies">Companies</NavLink>
      </Button>
    </div>
  );
  const searchInputComponent = (
    <SearchInput
      placeholder={isAccountsBlacklist ? "Search accounts" : "Search companies"}
      value={searchTerm}
      className="lg:max-w-72"
      variant={isDesktop ? "sm" : "lg"}
      onChange={(e) => setSearchTerm(e.target.value)}
      onClear={() => setSearchTerm("")}
    />
  );

  return (
    <NavTabsContentContainer
      desktopActionComponent={
        <AddToBlacklistButton
          type={blacklistType}
          size="sm"
          isEmptyPlaceholderButton={false}
        />
      }
      tabItems={CAMPAIGNS_TAB_ITEMS}
    >
      <MobileHeading
        extraComponent={navigationComponent}
        actionComponent={
          <>
            <AddToBlacklistButton
              type={blacklistType}
              size="md"
              isEmptyPlaceholderButton={false}
            />
            <RenderIf condition={!!selectedItemsCount}>
              {blacklistType === "accounts" ? (
                <ManageBlacklist
                  selectedItems={selectedAccounts}
                  resetSelection={() => setSelectedAccounts([])}
                  blacklistType={blacklistType}
                />
              ) : (
                <ManageBlacklist
                  selectedItems={selectedCompanies}
                  resetSelection={() => setSelectedCompanies([])}
                  blacklistType={blacklistType}
                />
              )}
            </RenderIf>
          </>
        }
        searchComponent={searchInputComponent}
      />
      <div className="flex gap-2 max-lg:hidden">
        {navigationComponent}
        {searchInputComponent}
      </div>
      {mainComponent}
    </NavTabsContentContainer>
  );
}
